import type { CellClassParams, ColDef, GridOptions } from "ag-grid-community"
import dayjs from "dayjs"

import { pricingCalc } from "../../../utils"
import { GridCells } from "../GridCells"
import type { PricingGridRow } from "../types"
import { FORMATTERS } from "../utils"

import { COLUMN_PROPS, MARGIN_THRESHOLDS, NON_EDITABLE_CELL_CLASS } from "./constants"

import { DATE_FORMAT } from "@/constants/formats"
import { formatEnumValue } from "@/constants/pricingTiers"
import { FreightTerms } from "@/graphql/codegen/graphql"

export const GRID_DEFAULT_OPTIONS: GridOptions<PricingGridRow> = {
  enableCellSpan: true,
  rowSelection: {
    mode: "multiRow",
    checkboxes: true,
    headerCheckbox: false,
  },
  selectionColumnDef: {
    pinned: "left",
    width: 50,
    sortable: false,
    resizable: false,
    headerClass: `${NON_EDITABLE_CELL_CLASS} border-r-1 border-r-gray-400`,
    headerComponent: GridCells.SelectionHeader,
    cellClass: "pricing-selection-cell border-r-1 border-r-gray-400",
    cellRenderer: GridCells.Selection,
  },
  domLayout: "autoHeight",
  dataTypeDefinitions: {
    dateString: {
      baseDataType: "dateString",
      extendsDataType: "dateString",
      valueParser: ({ newValue }) => newValue,
      valueFormatter: ({ value }) => value ?? "",
      dataTypeMatcher: (value) => typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value),
      dateParser: (value) => {
        if (!value) return undefined
        const parsed = dayjs(value, DATE_FORMAT.US, true)
        return parsed.isValid() ? parsed.toDate() : undefined
      },
      dateFormatter: (date) => {
        if (!date) return undefined
        return dayjs(date).format(DATE_FORMAT.US)
      },
    },
  },
}

const getNewMarginCellClass = (params: CellClassParams<PricingGridRow, number>): string => {
  const value = params.value as number
  if (value <= MARGIN_THRESHOLDS.LOW) return "bg-red-100"
  if (value <= MARGIN_THRESHOLDS.MEDIUM) return "bg-yellow-100"
  return "bg-green-100"
}

export const getPricingEditColumns = ({
  uomName,
  onAddPriceTier,
  onClearPriceTier,
  onEditSurcharge,
  getGroupIndex,
}: {
  uomName: string
  onAddPriceTier: (rowIndex: number) => void
  onClearPriceTier: (rowIndex: number) => void
  onEditSurcharge: (rowIndex: number) => void
  getGroupIndex: (priceChangeItemId: string) => number
}): ColDef<PricingGridRow>[] => {
  const groupAltCellClassRules: ColDef<PricingGridRow>["cellClassRules"] = {
    "!bg-gray-100": ({ data }) => {
      if (!data) return false
      return getGroupIndex(data.item.priceChangeItemId) % 2 === 1
    },
  }

  return [
    {
      colId: "customer",
      headerName: "Customer",
      ...COLUMN_PROPS.DEFAULT,
      width: 140,
      pinned: "left",
      spanRows: true,
      valueGetter: ({ data }) => data?.item?.customer?.name ?? "",
      cellClassRules: groupAltCellClassRules,
    },
    {
      colId: "shipTo",
      headerName: "Ship To",
      ...COLUMN_PROPS.DEFAULT,
      width: 140,
      pinned: "left",
      spanRows: true,
      valueGetter: ({ data }) => data?.item?.shipToLocationId ?? "",
      cellClassRules: groupAltCellClassRules,
    },
    {
      /**
       * The following warning is displayed. Functionality works, but violates an ag-grid constraint. Consider refactoring to avoid this if possible.
       * AG Grid: 'spanRows' requires 'editable' to be one of [false, undefined].
       */
      colId: "freight",
      headerName: "Freight",
      editable: true,
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.SPANNED,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: Object.values(FreightTerms),
        formatValue: formatEnumValue,
      },
      valueGetter: ({ data }) => data?.item?.freightTerms ?? "",
      valueSetter: ({ data, newValue }) => {
        data.item.freightTerms = newValue || null
        return true
      },
      valueFormatter: ({ value }) => formatEnumValue(value ?? ""),
      pinned: "left",
      width: 160,
      cellClassRules: groupAltCellClassRules,
    },
    {
      colId: "effectiveDate",
      headerName: "Effective",
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.DATE,
      ...COLUMN_PROPS.SPANNED,
      editable: true,
      valueGetter: ({ data }) => data?.item?.effectiveDate ?? "",
      valueSetter: ({ data, newValue }) => {
        data.item.effectiveDate = newValue || null
        return true
      },
      pinned: "left",
      cellClassRules: groupAltCellClassRules,
    },
    {
      colId: "expirationDate",
      headerName: "Expiration",
      editable: true,
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.DATE,
      ...COLUMN_PROPS.SPANNED,
      valueGetter: ({ data }) => data?.item?.expirationDate ?? "",
      valueSetter: ({ data, newValue }) => {
        data.item.expirationDate = newValue || null
        return true
      },
      pinned: "left",
      cellClassRules: groupAltCellClassRules,
    },
    {
      colId: "last12",
      headerName: "Last 12",
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) => data?.item?.last12MonthsQty ?? null,
      valueFormatter: FORMATTERS.uomSuffix(uomName),
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      field: "qty",
      headerName: "Qty",
      ...COLUMN_PROPS.DEFAULT,
      editable: true,
      valueFormatter: FORMATTERS.uomSuffix(uomName),
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      colId: "price",
      headerName: "Price",
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) => data?.price ?? null,
      valueFormatter: FORMATTERS.financial,
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      colId: "profit",
      headerName: "Profit",
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) => data?.profit ?? null,
      valueFormatter: FORMATTERS.financial,
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      colId: "margin",
      headerName: "Margin",
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) => data?.margin ?? null,
      valueFormatter: FORMATTERS.percentSuffix,
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      field: "newPrice",
      headerName: "New Price",
      ...COLUMN_PROPS.DEFAULT,
      editable: true,
      valueFormatter: FORMATTERS.financial,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      field: "newProfit",
      headerName: "New Profit",
      ...COLUMN_PROPS.DEFAULT,
      editable: true,
      valueFormatter: FORMATTERS.financial,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      field: "newMargin",
      headerName: "New Margin",
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.NUMERIC,
      valueFormatter: FORMATTERS.percentSuffix,
      cellClass: getNewMarginCellClass,
    },
    {
      colId: "surcharge",
      headerName: "Surcharge",
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) =>
        pricingCalc.surcharge({
          surcharge1Price: data?.surcharge1Price,
          surcharge2Price: data?.surcharge2Price,
          surcharge3Price: data?.surcharge3Price,
        }),
      valueFormatter: FORMATTERS.financial,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      colId: "bb",
      headerName: "BB",
      editable: true,
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.SPANNED,
      cellRenderer: GridCells.Bb,
      valueGetter: ({ data }) => data?.item?.billBack ?? null,
      valueSetter: ({ data, newValue }) => {
        data.item.billBack = newValue
        return true
      },
      ...COLUMN_PROPS.NUMERIC,
      cellClassRules: groupAltCellClassRules,
    },
    {
      headerName: "",
      ...COLUMN_PROPS.DEFAULT,
      cellRenderer: GridCells.RowActions,
      cellRendererParams: { onAddPriceTier, onClearPriceTier, onEditSurcharge },
      width: 60,
      suppressMovable: true,
      pinned: "right",
    },
  ]
}
